import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Form, Alert } from 'antd';
import { Select } from '../../antd';
import { getRecursiveVmFolders, getRecursiveResourcePools } from '../../../lib/inventoryHelpers';
import { useSourceInventoryPolling } from '../../../hooks';

// Cache stored OUTSIDE component to persist across remounts (until modal closes)
const globalCache = {
    appliances: {},
    datacenterDetails: {},
};

// Export function to clear cache - called ONLY when modal closes
export const clearVSphereCache = () => {
    globalCache.appliances = {};
    globalCache.datacenterDetails = {};
};

const VSphereFilterSelector = ({ applianceId, onFilterChange, userOrg, onCleanupRef }) => {
    const [selectedDatacenter, setSelectedDatacenter] = useState('');
    const [selectedCluster, setSelectedCluster] = useState('');
    const [selectedResourcePool, setSelectedResourcePool] = useState('');
    const [selectedFolder, setSelectedFolder] = useState('');
    const [datacenters, setDatacenters] = useState([]);
    const [datacenterDetails, setDatacenterDetails] = useState(null);
    const [isFetchingDetails, setIsFetchingDetails] = useState(false);

    // Flags to track when using cached data (to override loading states)
    const [isUsingCachedDatacenters, setIsUsingCachedDatacenters] = useState(false);
    const [isUsingCachedDetails, setIsUsingCachedDetails] = useState(false);

    // Local refs
    const lastFetchParamsRef = useRef({ applianceId: null, userOrg: null });
    const currentFetchingDatacenterRef = useRef(null);

    // Separate polling instances for datacenters and details
    const datacenterPolling = useSourceInventoryPolling();
    const detailsPolling = useSourceInventoryPolling();

    // Extract stable functions to prevent infinite loops
    const { reset: resetDatacenterPolling, triggerMetadataFetch: triggerDatacenterInventory } = datacenterPolling;
    const { reset: resetDetailsPolling, triggerMetadataFetch: triggerDetailsInventory } = detailsPolling;

    // Expose cleanup function to parent via ref - only resets component-local state
    useEffect(() => {
        if (onCleanupRef) {
            onCleanupRef.current = () => {
                resetDatacenterPolling();
                resetDetailsPolling();
                setDatacenters([]);
                setSelectedDatacenter('');
                setSelectedCluster('');
                setSelectedResourcePool('');
                setSelectedFolder('');
                setDatacenterDetails(null);
                setIsFetchingDetails(false);
                setIsUsingCachedDatacenters(false);
                setIsUsingCachedDetails(false);
                currentFetchingDatacenterRef.current = null;
            };
        }
    }, [onCleanupRef, resetDatacenterPolling, resetDetailsPolling]);

    // Initial fetch on mount - check cache first
    useEffect(() => {
        if (!applianceId || !userOrg) {
            return;
        }

        const hasParamsChanged =
            lastFetchParamsRef.current.applianceId !== applianceId || lastFetchParamsRef.current.userOrg !== userOrg;

        // Update last fetch params
        if (hasParamsChanged) {
            lastFetchParamsRef.current = { applianceId, userOrg };
            setDatacenterDetails(null);
            setSelectedDatacenter('');
            setSelectedCluster('');
            setSelectedResourcePool('');
            setSelectedFolder('');
        }

        // Check if we have cached appliance datacenter list for this specific appliance
        const cacheKey = `${applianceId}-${userOrg}`;

        if (globalCache.appliances[cacheKey]) {
            // Use cached data
            const cachedData = globalCache.appliances[cacheKey];

            // Reset polling and set flag to override loading state
            resetDatacenterPolling();
            setIsUsingCachedDatacenters(true);
            setDatacenters(cachedData);

            if (cachedData.length === 1) {
                setSelectedDatacenter(cachedData[0].name);
            }
            return;
        }

        // No cache - fetch from API
        setIsUsingCachedDatacenters(false);
        const init = async () => {
            try {
                resetDatacenterPolling();
                setDatacenters([]);

                await triggerDatacenterInventory({
                    userOrg,
                    applianceId,
                    objectsToCollect: ['datacenter'],
                });
            } catch (err) {
                console.error('Error fetching datacenters:', err);
            }
        };

        init();
    }, [applianceId, userOrg]);

    // Handle datacenter polling completion and cache the result
    useEffect(() => {
        if (datacenterPolling.isComplete && datacenterPolling.inventoryStatus?.properties?.datacenters) {
            const datacenterList = datacenterPolling.inventoryStatus.properties.datacenters;
            setDatacenters(datacenterList);

            // Cache for this specific appliance
            const cacheKey = `${applianceId}-${userOrg}`;
            globalCache.appliances[cacheKey] = datacenterList;

            if (datacenterList.length === 1) {
                setSelectedDatacenter(datacenterList[0].name);
            }
        }
    }, [datacenterPolling.isComplete, datacenterPolling.inventoryStatus, applianceId, userOrg]);

    // Handle details polling completion and cache the result
    useEffect(() => {
        if (detailsPolling.isComplete && detailsPolling.inventoryStatus?.properties?.datacenters?.[0]) {
            const details = detailsPolling.inventoryStatus.properties.datacenters[0];
            const fetchedDatacenter = currentFetchingDatacenterRef.current;

            if (fetchedDatacenter) {
                // Cache with appliance-specific key
                const cacheKey = `${applianceId}-${fetchedDatacenter}`;
                globalCache.datacenterDetails[cacheKey] = details;

                // Only update UI if this is still the selected datacenter
                if (fetchedDatacenter === selectedDatacenter) {
                    setDatacenterDetails(details);
                }

                setIsFetchingDetails(false);
            }

            currentFetchingDatacenterRef.current = null;
        }

        if (detailsPolling.hasError) {
            setIsFetchingDetails(false);
            currentFetchingDatacenterRef.current = null;
        }
    }, [
        detailsPolling.isComplete,
        detailsPolling.hasError,
        detailsPolling.inventoryStatus,
        selectedDatacenter,
        applianceId,
    ]);

    // Auto-fetch details when datacenter selected (use cache if available)
    useEffect(() => {
        if (!selectedDatacenter) {
            setDatacenterDetails(null);
            setIsFetchingDetails(false);
            currentFetchingDatacenterRef.current = null;
            return;
        }

        // Check cache with appliance-specific key
        const cacheKey = `${applianceId}-${selectedDatacenter}`;

        if (globalCache.datacenterDetails[cacheKey]) {
            // Reset polling and set flag to override loading state
            resetDetailsPolling();
            setIsUsingCachedDetails(true);
            setDatacenterDetails(globalCache.datacenterDetails[cacheKey]);
            setIsFetchingDetails(false);
            currentFetchingDatacenterRef.current = null;
            return;
        }

        setIsUsingCachedDetails(false);
        currentFetchingDatacenterRef.current = selectedDatacenter;
        setIsFetchingDetails(true);
        setDatacenterDetails(null);

        const fetchDetails = async () => {
            try {
                resetDetailsPolling();

                await triggerDetailsInventory({
                    userOrg,
                    applianceId,
                    datacenterName: selectedDatacenter,
                    objectsToCollect: ['cluster', 'vm_folder', 'resource_pool'],
                });
            } catch (err) {
                console.error('Error fetching datacenter details:', err);
                setIsFetchingDetails(false);
                currentFetchingDatacenterRef.current = null;
            }
        };

        fetchDetails();
    }, [selectedDatacenter, applianceId]);

    // Update filter params whenever selections change
    useEffect(() => {
        if (selectedDatacenter) {
            onFilterChange?.({
                datacenter_name: selectedDatacenter,
                cluster_name: selectedCluster || undefined,
                resource_pool: selectedResourcePool || undefined,
                folder_name: selectedFolder || undefined,
            });
        } else {
            onFilterChange?.(null);
        }
    }, [selectedDatacenter, selectedCluster, selectedResourcePool, selectedFolder, onFilterChange]);

    const handleDatacenterChange = useCallback((value) => {
        setSelectedCluster('');
        setSelectedResourcePool('');
        setSelectedFolder('');
        setSelectedDatacenter(value);
    }, []);

    const handleClusterChange = useCallback((value) => {
        setSelectedCluster(value);
        // Reset dependent fields
        setSelectedResourcePool('');
    }, []);

    const handleResourcePoolChange = useCallback((value) => {
        setSelectedResourcePool(value);
    }, []);

    const handleFolderChange = useCallback((value) => {
        setSelectedFolder(value);
    }, []);

    // Get datacenter options (sorted alphabetically)
    const datacenterOptions = useMemo(
        () =>
            datacenters
                .map((dc) => ({
                    value: dc.name,
                    label: dc.name,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [datacenters],
    );

    // Get cluster options from selected datacenter (sorted alphabetically)
    const clusterOptions = useMemo(() => {
        if (!datacenterDetails?.clusters) return [];
        return datacenterDetails.clusters
            .map((cluster) => ({
                value: cluster.name,
                label: cluster.name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [datacenterDetails]);

    // Get resource pool options from selected cluster (sorted alphabetically)
    const resourcePoolOptions = useMemo(() => {
        const selectedClusterData = datacenterDetails?.clusters?.find((c) => c.name === selectedCluster);
        if (!selectedClusterData?.resource_pools) {
            return [];
        }

        const recursivePools = getRecursiveResourcePools(selectedClusterData.resource_pools);
        return recursivePools
            .map((pool) => ({
                value: pool.name,
                label: pool.name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [datacenterDetails, selectedCluster]);

    // Get folder options from datacenter (sorted alphabetically)
    const folderOptions = useMemo(() => {
        if (!datacenterDetails?.vm_folder?.folders) {
            return [];
        }

        const recursiveFolders = getRecursiveVmFolders(datacenterDetails.vm_folder.folders);
        return recursiveFolders
            .map((folder) => ({
                value: folder.name,
                label: folder.name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [datacenterDetails]);

    if (datacenterPolling.hasError || detailsPolling.hasError) {
        return <Alert title="Error loading filter parameters." type="error" showIcon style={{ marginBottom: 16 }} />;
    }

    // Determine actual loading states (don't show loading when using cached data)
    const isLoadingDatacenters = datacenterPolling.isPolling && !isUsingCachedDatacenters;
    const isLoadingDetails = isFetchingDetails && !isUsingCachedDetails;

    return (
        <div className="vsphere-filter-selector" style={{ marginBottom: 16 }}>
            <Form.Item label="Datacenter">
                <Select
                    placeholder={isLoadingDatacenters ? 'Loading datacenters...' : 'Select Datacenter'}
                    value={selectedDatacenter || undefined}
                    onChange={handleDatacenterChange}
                    options={datacenterOptions}
                    allowClear
                    disabled={isLoadingDatacenters}
                    loading={isLoadingDatacenters}
                />
            </Form.Item>

            {selectedDatacenter && (
                <>
                    <Form.Item label="Cluster">
                        <Select
                            placeholder={isLoadingDetails ? 'Loading clusters...' : 'Select Cluster (optional)'}
                            value={selectedCluster || undefined}
                            onChange={handleClusterChange}
                            options={clusterOptions}
                            allowClear
                            disabled={isLoadingDetails}
                            loading={isLoadingDetails}
                        />
                    </Form.Item>

                    {selectedCluster && !isLoadingDetails && (
                        <Form.Item label="Resource Pool">
                            <Select
                                placeholder="Select Resource Pool (optional)"
                                value={selectedResourcePool || undefined}
                                onChange={handleResourcePoolChange}
                                options={resourcePoolOptions}
                                allowClear
                            />
                        </Form.Item>
                    )}

                    {!isLoadingDetails && (
                        <Form.Item label="Folder">
                            <Select
                                placeholder="Select Folder (optional)"
                                value={selectedFolder || undefined}
                                onChange={handleFolderChange}
                                options={folderOptions}
                                allowClear
                            />
                        </Form.Item>
                    )}
                </>
            )}
        </div>
    );
};

export default VSphereFilterSelector;
